import { useEffect, useMemo, useState, useCallback } from 'react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { Loader2, X, Scale } from 'lucide-react'
import type { CompetencyAssignment } from './CompetencySelector'

type EntityType = 'building_block' | 'learning_plan' | 'task'

interface BaseAssignmentDetail {
  assignment_id: number
  competency_id: number
  weight: number | null
  required_for_competency: number | boolean | null
  auto_assigned: number | boolean | null
  assignment_source: string | null
  notes: string | null
  updated_at: string | null
  created_at: string | null
}

interface BuildingBlockDetail extends BaseAssignmentDetail {
  activity_id: number
}

interface LearningPlanDetail extends BaseAssignmentDetail {
  lpid: number
  lernplan_name: string
  sortierung: number | null
}

interface TaskDetail extends BaseAssignmentDetail {
  aid: number
  task_name: string
  sortierung: number | null
  lpid: number
  lernplan_name: string
}

interface BuildingBlockDetailsResponse {
  entity: BuildingBlockDetail | null
  learningPlans: LearningPlanDetail[]
  tasks: TaskDetail[]
}

interface LearningPlanDetailsResponse {
  entity: LearningPlanDetail | null
  tasks: TaskDetail[]
}

interface TaskDetailsResponse {
  entity: TaskDetail | null
}

type DetailsResponse =
  | BuildingBlockDetailsResponse
  | LearningPlanDetailsResponse
  | TaskDetailsResponse

interface CompetencyDetailsModalProps {
  isOpen: boolean
  entityType: EntityType
  entityId: number
  assignment: CompetencyAssignment | null
  onClose: () => void
  onRefreshAssignments: () => Promise<unknown>
}

const sourceLabels: Record<string, string> = {
  manual: 'Manuell',
  llm_direct: 'Automatisch (LLM direkt)',
  llm_fallback_lernplan: 'Automatisch (Lernplan)',
  llm_fallback_baustein: 'Automatisch (Baustein)',
  aggregated_from_learning_plans: 'Aggregiert aus Lernplänen',
  aggregated_from_tasks: 'Aggregiert aus Aufgaben',
  direct_analysis: 'Analyse',
  inherited_from_learning_plan: 'Vererbt vom Lernplan',
  inherited_from_building_block: 'Vererbt vom Baustein',
  bottom_up_task: 'Bottom-Up (Aufgabe)',
  bottom_up_learning_plan: 'Bottom-Up (Lernplan)',
  top_down_building_block: 'Top-Down (Baustein)',
  top_down_task: 'Top-Down (Aufgabe)'
}

const formatSourceLabel = (value: string | null) => {
  if (!value) return 'Unbekannt'
  return sourceLabels[value] || value
}

const formatWeightValue = (value: number | null) =>
  value === null || value === undefined ? '' : Number(value).toFixed(2)

const parseWeightInput = (value: string) => {
  if (value.trim() === '') return null
  const parsed = Number(value)
  if (Number.isNaN(parsed)) return null
  return parsed
}

export default function CompetencyDetailsModal({
  isOpen,
  entityType,
  entityId,
  assignment,
  onClose,
  onRefreshAssignments
}: CompetencyDetailsModalProps) {
  const [loading, setLoading] = useState(false)
  const [details, setDetails] = useState<DetailsResponse | null>(null)
  const [weightValues, setWeightValues] = useState<Record<string, string>>({})
  const [savingKey, setSavingKey] = useState<string | null>(null)

  const competencyName = assignment?.competency_name || 'Kompetenz'

  const createWeightMap = useCallback((data: DetailsResponse) => {
    const weights: Record<string, string> = {}
    const assignWeight = (assignmentId: number | undefined, weight: number | null | undefined) => {
      if (!assignmentId) return
      weights[`assignment-${assignmentId}`] = formatWeightValue(weight ?? null)
    }

    if ('entity' in data && data.entity) {
      assignWeight(data.entity.assignment_id, data.entity.weight ?? null)
    }

    if ('learningPlans' in data) {
      data.learningPlans.forEach(lp => assignWeight(lp.assignment_id, lp.weight ?? null))
    }

    if ('tasks' in data) {
      data.tasks.forEach(task => assignWeight(task.assignment_id, task.weight ?? null))
    }

    return weights
  }, [])

  const applyDetails = useCallback(
    (data: DetailsResponse) => {
      setDetails(data)
      setWeightValues(createWeightMap(data))
    },
    [createWeightMap]
  )

  const detailKeys = useMemo(() => {
    const keys: string[] = []
    if (details) {
      const pushKey = (assignmentId?: number) => {
        if (assignmentId) {
          keys.push(`assignment-${assignmentId}`)
        }
      }

      if ('entity' in details && details.entity) {
        pushKey(details.entity.assignment_id)
      }

      if ('learningPlans' in details) {
        details.learningPlans.forEach(lp => pushKey(lp.assignment_id))
      }

      if ('tasks' in details) {
        details.tasks.forEach(task => pushKey(task.assignment_id))
      }
    }
    return keys
  }, [details])

  useEffect(() => {
    if (!isOpen || !assignment) {
      setDetails(null)
      setWeightValues({})
      return
    }

    const fetchDetails = async () => {
      try {
        setLoading(true)
        const response = await api.get(
          `/competency-assignments/${entityType}/${entityId}/${assignment.competency_id}/details`
        )
        if (response.data.success) {
          applyDetails(response.data.data)
        }
      } catch (error: any) {
        console.error('Error fetching competency details:', error)
        toast.error('Fehler beim Laden der Kompetenz-Details')
      } finally {
        setLoading(false)
      }
    }

    fetchDetails()
  }, [applyDetails, assignment, entityId, entityType, isOpen])

  const handleWeightInputChange = (key: string, value: string) => {
    setWeightValues((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSaveWeight = async (
    key: string,
    contextType: EntityType,
    contextId: number,
    originalWeight: number | null
  ) => {
    if (!assignment) return
    const inputValue = weightValues[key] ?? ''
    const parsedWeight = parseWeightInput(inputValue)
    const normalizedOriginal = originalWeight === null ? null : Number(originalWeight)

    if (parsedWeight === null) {
      toast.error('Bitte eine gültige Gewichtung zwischen 0 und 10 angeben')
      return
    }

    if (parsedWeight === normalizedOriginal) {
      toast('Keine Änderung in der Gewichtung', { icon: 'ℹ️' })
      return
    }

    if (parsedWeight < 0 || parsedWeight > 10) {
      toast.error('Gewichtung muss zwischen 0 und 10 liegen')
      return
    }

    try {
      setSavingKey(key)
      await api.patch(
        `/competency-assignments/${contextType}/${contextId}/${assignment?.competency_id}`,
        {
          weight: parsedWeight
        }
      )
      toast.success('Gewichtung aktualisiert')
      await onRefreshAssignments()
      // Reload details to reflect latest values
      const response = await api.get(
        `/competency-assignments/${entityType}/${entityId}/${assignment?.competency_id}/details`
      )
      if (response.data.success) {
        applyDetails(response.data.data)
      }
    } catch (error: any) {
      console.error('Error updating weight:', error)
      toast.error('Fehler beim Aktualisieren der Gewichtung')
    } finally {
      setSavingKey(null)
    }
  }

  if (!isOpen || !assignment) {
    return null
  }

  const renderWeightControl = (
    key: string,
    label: string,
    contextType: EntityType,
    contextId: number,
    detail: BaseAssignmentDetail
  ) => {
    const currentWeight =
      detail.weight === null || detail.weight === undefined ? null : Number(detail.weight)
    const inputValue = weightValues[key] ?? formatWeightValue(detail.weight ?? null)
    const isSaving = savingKey === key

    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Scale className="h-4 w-4 text-gray-400" />
          <span>{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.1"
            min="0"
            max="10"
            className="w-24 rounded border border-gray-300 px-3 py-1 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            value={inputValue}
            onChange={(e) => handleWeightInputChange(key, e.target.value)}
            placeholder="Gewicht"
            disabled={isSaving}
          />
          <button
            type="button"
            onClick={() => handleSaveWeight(key, contextType, contextId, currentWeight)}
            className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Speichern...
              </>
            ) : (
              'Speichern'
            )}
          </button>
        </div>
      </div>
    )
  }

  const renderSourceInfo = (detail: BaseAssignmentDetail) => (
    <div className="mt-1 text-xs text-gray-500">
      Quelle: {formatSourceLabel(detail.assignment_source)}
      {detail.auto_assigned ? ' • automatisch' : ''}
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Kompetenz-Details: {competencyName}
            </h2>
            <p className="text-sm text-gray-500">
              Übersicht aller Zuordnungen auf Baustein-, Lernplan- und Aufgabenebene
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Lade Details...</span>
            </div>
          ) : (
            <>
              {'entity' in (details || {}) && details?.entity && (
                <section className="mb-6 rounded-lg border border-blue-100 bg-blue-50/60 p-4">
                  <h3 className="text-sm font-semibold text-blue-900">
                    {entityType === 'building_block'
                      ? 'Baustein'
                      : entityType === 'learning_plan'
                      ? 'Lernplan'
                      : 'Aufgabe'}
                  </h3>
                  {renderSourceInfo(details.entity)}
                  {renderWeightControl(
                    `assignment-${details.entity.assignment_id}`,
                    'Gewichtung dieser Zuordnung',
                    entityType,
                    entityType === 'building_block'
                      ? (details.entity as BuildingBlockDetail).activity_id
                      : entityType === 'learning_plan'
                      ? (details.entity as LearningPlanDetail).lpid
                      : (details.entity as TaskDetail).aid,
                    details.entity
                  )}
                </section>
              )}

              {'learningPlans' in (details || {}) && (details as BuildingBlockDetailsResponse)?.learningPlans.length ? (
                <section className="mb-6 rounded-lg border border-green-100 bg-green-50/60 p-4">
                  <h3 className="text-sm font-semibold text-green-900">Lernpläne</h3>
                  <div className="mt-3 space-y-3">
                    {(details as BuildingBlockDetailsResponse).learningPlans.map((lp) => {
                      const key = `assignment-${lp.assignment_id}`
                      return (
                        <div
                          key={lp.assignment_id}
                          className="rounded-lg border border-green-200 bg-white px-4 py-3 shadow-sm"
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {lp.lernplan_name} (LPID: {lp.lpid})
                          </div>
                          {renderSourceInfo(lp)}
                          {renderWeightControl(
                            key,
                            'Gewichtung dieses Lernplans',
                            'learning_plan',
                            lp.lpid,
                            lp
                          )}
                        </div>
                      )
                    })}
                  </div>
                </section>
              ) : null}

              {'tasks' in (details || {}) && (details as BuildingBlockDetailsResponse | LearningPlanDetailsResponse).tasks.length ? (
                <section className="rounded-lg border border-purple-100 bg-purple-50/60 p-4">
                  <h3 className="text-sm font-semibold text-purple-900">Aufgaben</h3>
                  <div className="mt-3 space-y-3">
                    {(details as BuildingBlockDetailsResponse | LearningPlanDetailsResponse).tasks.map((task) => {
                      const key = `assignment-${task.assignment_id}`
                      return (
                        <div
                          key={task.assignment_id}
                          className="rounded-lg border border-purple-200 bg-white px-4 py-3 shadow-sm"
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {task.task_name} (AID: {task.aid})
                          </div>
                          {'lernplan_name' in task && task.lernplan_name && (
                            <div className="text-xs text-gray-500">
                              Lernplan: {task.lernplan_name} (LPID: {task.lpid})
                            </div>
                          )}
                          {renderSourceInfo(task)}
                          {renderWeightControl(
                            key,
                            'Gewichtung dieser Aufgabe',
                            'task',
                            task.aid,
                            task
                          )}
                        </div>
                      )
                    })}
                  </div>
                </section>
              ) : null}

              {!loading && details && detailKeys.length === 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                  Keine Zuordnungen für diese Kompetenz gefunden.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}


